import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../../../components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '../../../../components/ui/alert';
import { Loader2, AlertCircle, CheckCircle2, Rocket, Copy } from 'lucide-react'; // <-- 1. ADD Copy ICON
import { useAdminStore } from '../../../../store/adminStore';

import useCategoryDraftQuestions from '../../../../hooks/useCategoryDraftQuestions';
import LeadCaptureModal from '../../../Leads/LeadCaptureModal';
import api from '../../../../services/apiServices';

export default function PublishView({ categoryId, fetchQuestions: refetchParentQuestions }) {
  const [previewTriggered, setPreviewTriggered] = useState(true); 
  const [isPublishing, setIsPublishing] = useState(false);
  const [generatedUrlString, setGeneratedUrlString] = useState(''); // <-- 2. ADD STATE

  const { toast } = useToast();
  const { questions, loading: previewLoading, error: previewError, refetch: refetchPreview } = useCategoryDraftQuestions(categoryId, previewTriggered);

  const hasUnpublishedChanges = questions?.some(q => q.dynamodb_update === false || q.dynamodb_update === undefined);
  const hasSuccessfullyPublishedOnce = questions?.every(q => q.dynamodb_update === true) && questions.length > 0;
  const locale = useAdminStore((state) => state.locale);

  // Warn (don't block) when Test-tagged questions exist but A/B testing isn't
  // enabled — publish will correctly exclude them, but that's easy to mistake
  // for "my test question disappeared" without this heads-up.
  const [abTestEnabled, setAbTestEnabled] = useState(null);
  useEffect(() => {
    if (!categoryId) return;
    api.get(`/admin/categories/cat-qna/ab-config/${categoryId}`, { params: { locale } })
      .then(res => setAbTestEnabled(res.data.ab_test_enabled))
      .catch(() => setAbTestEnabled(null)); // non-critical; just skip the warning if this fails
  }, [categoryId, locale]);
  const untestedTestQuestionCount = questions?.filter(q => q.ab_variant === 'test').length || 0;
  const showUntestedTestWarning = abTestEnabled === false && untestedTestQuestionCount > 0;

  // <-- 3. ADD THIS CALLBACK FUNCTION -->
  const handlePreviewSubmission = useCallback((urlParams) => {
    // Check if this is a reset signal from the modal's "Test Again" button
    if (urlParams === null) {
      setGeneratedUrlString('');
      return;
    }

    // Handle case where user clicked "Generate URL" with no answers
    if (Object.keys(urlParams).length === 0) {
      toast({
        variant: "destructive",
        title: "No URL to Generate",
        description: "You haven't answered any questions that can be pre-filled.",
      });
      setGeneratedUrlString('');
      return;
    }
    
    // Convert the object { q230: "a130" } into a string "q230=a130"
    const queryString = new URLSearchParams(urlParams).toString();
    
    setGeneratedUrlString(queryString);

    // Give user feedback and copy to clipboard
    try {
      navigator.clipboard.writeText(queryString);
      toast({
        title: "Preview URL Generated!",
        description: "The pre-fill string has been copied to your clipboard.",
      });
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
      toast({
        variant: "default",
        title: "Preview URL Generated",
        description: "Here is your pre-fill string (failed to auto-copy).",
      });
    }
  }, [toast]); // Dependency on toast

  const handlePublish = useCallback(async () => {
    // ... (This function remains unchanged)
    setIsPublishing(true);
    console.log("Question posting to DynamoDB:", questions);

    try {
      const res = await api.post(`/admin/categories/cat-qna/ddb/${categoryId}`, { questions },{
        params: { locale },
      });

      toast({
        title: "Publish Successful!",
        description: "Q&A has been published to DynamoDB.",
        variant: "default",
      });

      await refetchPreview(); 

      if (refetchParentQuestions) {
        refetchParentQuestions();
      }
    } catch (err) {
      console.error("Publish error:", err);
      toast({
        variant: "destructive",
        title: "Publish Error",
        description:
          err?.response?.data?.message || err.message || "An unexpected error occurred while publishing.",
      });
    } finally {
      setIsPublishing(false);
    }
  }, [categoryId, questions, refetchPreview, toast, refetchParentQuestions, locale]); // Added locale


  const renderStatusAlert = () => {
    // ... (This function remains unchanged)
    if (previewLoading) return null;
    if (previewError) return null;

    if (questions && questions.length === 0 && !previewLoading) {
        return (
            <Alert variant="default" className="mb-6">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>No Questions</AlertTitle>
                <AlertDescription>
                    There are no questions in the draft to publish. Please add questions in the "Edit" tab first.
                </AlertDescription>
            </Alert>
        );
    }

    if (hasSuccessfullyPublishedOnce && !hasUnpublishedChanges) {
      return (
        <Alert variant="default" className="mb-6 bg-green-50 border-green-300 dark:bg-green-900/20 dark:border-green-700">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertTitle className="text-green-700 dark:text-green-300">All Published</AlertTitle>
          <AlertDescription className="text-green-600 dark:text-green-500">
            All questions are currently synchronized with DynamoDB.
          </AlertDescription>
        </Alert>
      );
    } else if (hasUnpublishedChanges) {
      return (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Unpublished Changes</AlertTitle>
          <AlertDescription>
            Some questions have pending changes that are not yet live in DynamoDB.
            Review the preview and click "Publish" to update.
          </AlertDescription>
        </Alert>
      );
    } else {
       return (
        <Alert variant="default" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Ready to Publish</AlertTitle>
            <AlertDescription>
                Review the preview and click "Publish to DynamoDB" to make the questions live.
            </AlertDescription>
        </Alert>
       );
    }
  };


  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center text-xl">
          <Rocket className="mr-2 h-5 w-5" /> Publish to Live
        </CardTitle>
        <CardDescription>
          Preview how your Q&A will appear and publish the current draft to the live DynamoDB database.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showUntestedTestWarning && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Test-tagged questions will be excluded</AlertTitle>
            <AlertDescription>
              {untestedTestQuestionCount} question{untestedTestQuestionCount > 1 ? 's are' : ' is'} tagged "Test only"
              but A/B testing is disabled for this category + locale. They will NOT be published to any buyer.
              Enable A/B testing in the "A/B Testing" tab first if you want them live.
            </AlertDescription>
          </Alert>
        )}
        {renderStatusAlert()}

        {previewLoading && (
          // ... (loading spinner, unchanged)
          <div className="flex flex-col items-center justify-center h-60 border border-dashed border-border rounded-md">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-muted-foreground">Loading Preview...</p>
          </div>
        )}

        {previewError && !previewLoading && (
          // ... (error alert, unchanged)
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Loading Preview</AlertTitle>
            <AlertDescription>
              Could not load the Q&A preview. Please try again. (Error: {previewError.message})
              <Button variant="outline" size="sm" onClick={() => refetchPreview()} className="ml-4">Try Again</Button>
            </AlertDescription>
          </Alert>
        )}

        {!previewLoading && !previewError && questions && questions.length > 0 && (
          <div className="mb-6 p-4 border rounded-md bg-muted/20">
            <h4 className="text-lg font-semibold mb-3 text-foreground">Live Preview:</h4>
            <LeadCaptureModal
              categoryId={categoryId}
              questions={questions}
              runType="test"
              onClose={() => {}}
              isModal={false}
              onPreviewComplete={handlePreviewSubmission} // <-- 4. PASS THE PROP
              locale={locale}
            />
          </div>
        )}
        
        {/* --- 5. ADD THIS NEW BLOCK TO DISPLAY THE URL --- */}
        {generatedUrlString && (
          <div className="mt-6 p-4 border border-dashed border-primary/50 rounded-md bg-muted">
            <h4 className="text-lg font-semibold mb-2 text-foreground">Generated Pre-fill String</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Append this string to your website URL (after a `?`) to pre-fill the Q&A.
            </p>
            <div className="flex gap-2 p-3 bg-background rounded-md font-mono text-sm break-all shadow-inner">
              {generatedUrlString}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => {
                navigator.clipboard.writeText(generatedUrlString);
                toast({ title: "Copied to clipboard!" });
              }}
            >
              <Copy className="mr-2 h-4 w-4" /> Copy String
            </Button>
          </div>
        )}
        {/* --- END NEW BLOCK --- */}

      </CardContent>
      <CardFooter className="flex flex-col items-center gap-4">
        <Button 
          onClick={handlePublish} 
          disabled={isPublishing || previewLoading || !!previewError || !questions || questions.length === 0}
          size="lg"
          className="w-full sm:w-auto"
        >
          {isPublishing ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Rocket className="mr-2 h-4 w-4" />
          )}
          {isPublishing ? 'Publishing...' : 'Publish to DynamoDB'}
        </Button>
      </CardFooter>
    </Card>
  );
}